import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, ShieldCheck, AlertTriangle, Link2, Loader2, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const RISK = {
  dangerous: { label: "Dangerous", cls: "border-destructive/40 bg-destructive/10 text-destructive", Icon: ShieldAlert },
  danger: { label: "Dangerous", cls: "border-destructive/40 bg-destructive/10 text-destructive", Icon: ShieldAlert },
  caution: { label: "Use caution", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400", Icon: AlertTriangle },
  warning: { label: "Use caution", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400", Icon: AlertTriangle },
  safe: { label: "Looks safe", cls: "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint", Icon: ShieldCheck },
};

const LINK_STYLE = {
  danger: "border-destructive/40 bg-destructive/10 text-destructive",
  dangerous: "border-destructive/40 bg-destructive/10 text-destructive",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  safe: "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint",
  unknown: "border-white/12 bg-bloq-s2 text-bloq-muted",
};

export function ShieldExplainDialog({ open, onOpenChange, text }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["shield-explain", text],
    queryFn: () => api.shieldExplain(text),
    enabled: open && !!text,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const risk = RISK[data?.risk_level] || RISK.caution;
  const links = data?.heuristics?.links || [];
  const indicators = data?.indicators || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="shield-explain-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-4 w-4 text-bloq-mint" /> Shield explainer
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Plain-language read on why this message may (or may not) be risky.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-bloq-muted" data-testid="shield-explain-error">
            Couldn't analyze this message right now. Please try again.
          </p>
        ) : (
          <div className="space-y-4" data-testid="shield-explain-result">
            <div className={cn("flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold", risk.cls)} data-testid="shield-explain-verdict">
              <risk.Icon className="h-5 w-5 shrink-0" />
              {risk.label}
            </div>

            {data?.explanation && (
              <p className="text-sm leading-relaxed text-white/90" data-testid="shield-explain-summary">{data.explanation}</p>
            )}

            {indicators.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">What Shield noticed</div>
                <ul className="space-y-1.5">
                  {indicators.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85" data-testid={`shield-indicator-${i}`}>
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {links.map((l, i) => (
              <div key={i} className={cn("flex items-start gap-2 rounded-xl border px-3 py-2 text-sm", LINK_STYLE[l.verdict] || LINK_STYLE.unknown)}>
                <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs">{l.url}</div>
                  <div className="opacity-90">{l.label}</div>
                </div>
              </div>
            ))}

            {data?.advice && (
              <div className="flex items-start gap-2 rounded-xl border border-bloq-mint/25 bg-bloq-mint/10 px-4 py-3 text-sm text-bloq-mint" data-testid="shield-explain-advice">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{data.advice}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
